import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";

/**
 * Carrousel éditorial / FAQ premium
 * - Ton contenu stratégique (coaching vs thérapie, interculturel, etc.)
 * - Auto-play doux
 * - RTL-compatible
 * - Style papyrus existant
 */
const EditorialCarousel = ({ autoplay = true, interval = 9000 }) => {
  const { t, i18n } = useTranslation();
  const dir = i18n.dir();
  const isRTL = dir === "rtl";

  // Slides = FAQ premium
  // Chaque item utilise des clés i18n dédiées "faq.sX.title" + "faq.sX.body"
  const slides = useMemo(
    () => [
      {
        kicker: t("faq.kicker", "Questions fréquentes"),
        title: t(
          "faq.s1.title",
          "Quelle est la différence entre coaching et thérapie ?"
        ),
        body: t(
          "faq.s1.body",
          "Le coaching se concentre sur l'atteinte d'objectifs futurs et le développement de ton potentiel, tandis que la thérapie traite principalement les blessures du passé. Mon approche coaching t'aide à créer la vie que tu souhaites en partant de tes ressources actuelles."
        ),
      },
      {
        kicker: t("faq.kicker", "Questions fréquentes"),
        title: t(
          "faq.s2.title",
          "Pourquoi choisir un coaching interculturel spécialisé ?"
        ),
        body: t(
          "faq.s2.body",
          "Parce que ta réalité multiculturelle est une richesse, pas un obstacle. Je comprends intimement les défis spécifiques aux femmes qui naviguent entre plusieurs cultures, langues et systèmes de valeurs. Cette expertise unique permet un accompagnement authentique et profond."
        ),
      },
      {
        kicker: t("faq.kicker", "Questions fréquentes"),
        title: t(
          "faq.s3.title",
          "Comment trouver le bon allié ?"
        ),
        body: t(
          "faq.s3.body",
          "Il doit avoir l'humilité de ne pas connaître ta vie mieux que toi, mais l'expertise pour en déverrouiller le potentiel. Il doit comprendre les non-dits de ta culture, le poids d'un regard, la valeur d'un silence. Il doit allier la rigueur d'une méthode à la chaleur d'un cœur qui comprend."
        ),
      },
      {
        kicker: t("faq.kicker", "Questions fréquentes"),
        title: t(
          "faq.s4.title",
          "Combien de séances sont généralement nécessaires ?"
        ),
        body: t(
          "faq.s4.body",
          "Cela dépend entièrement de tes objectifs et de ton rythme. En général, un accompagnement efficace demande 6 à 10 séances, espacées de 2 à 3 semaines pour permettre une intégration réelle. Certaines transformations se font plus vite, d'autres demandent plus de temps — on s'adapte à ta réalité."
        ),
      },
    ],
    [t]
  );

  const [index, setIndex] = useState(0);
  const timer = useRef(null);

  // Aller à la slide suivante/précédente
  const go = (dirStep) => {
    setIndex((prev) => {
      const next = (prev + dirStep + slides.length) % slides.length;
      return next;
    });
  };

  // Aller à une slide précise (dots)
  const to = (i) => setIndex(i);

  // Auto-play
  useEffect(() => {
    if (!autoplay) return;
    timer.current = setInterval(() => go(1), interval);
    return () => clearInterval(timer.current);
  }, [autoplay, interval, slides.length]);

  return (
    <section
      id="editorial"
      className="relative py-16 md:py-24 papyrus-bg overflow-hidden"
      dir={dir}
      aria-label="Editorial carousel / FAQ"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Carte 'papier' existante */}
        <div className="paper-wrap p-6 sm:p-10 md:p-14 bg-white/60">
          {/* Kicker + icône */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <Quote className="h-6 w-6 text-warm-sage" />
            <span className="uppercase tracking-[0.18em] text-[12px] text-warm-gray">
              {slides[index]?.kicker}
            </span>
          </div>

          {/* Titre (la "question") */}
          <h2
            className="
              text-center font-serif font-light text-charcoal tracking-tight
              text-3xl md:text-4xl lg:text-[40px] leading-snug mb-8
            "
          >
            {slides[index]?.title}
          </h2>

          {/* Corps (la réponse) */}
          <div className="max-w-3xl mx-auto">
            <p className="text-[17px] md:text-[18px] leading-[1.9] text-warm-gray text-center">
              {slides[index]?.body}
            </p>
          </div>

          {/* Navigation bas (flèches + dots) */}
          <div className="mt-10 md:mt-12 flex items-center justify-between">
            {/* Précédent */}
            <button
              type="button"
              className="carousel-nav-btn"
              aria-label={t("faq.prev", "Précédent")}
              onClick={() => go(isRTL ? 1 : -1)}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            {/* Dots état actif */}
            <div className="flex items-center gap-3">
              {slides.map((_, i) => (
                <button
                  key={i}
                  aria-label={`Aller à ${i + 1}`}
                  onClick={() => to(i)}
                  className={`paper-dot ${
                    i === index ? "paper-dot--active" : ""
                  }`}
                />
              ))}
            </div>

            {/* Suivant */}
            <button
              type="button"
              className="carousel-nav-btn"
              aria-label={t("faq.next", "Suivant")}
              onClick={() => go(isRTL ? -1 : 1)}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EditorialCarousel;
